import React, { PropsWithChildren } from 'react';
import cls from 'classnames';
import './EggTabs.scss';
import { NavLink } from 'react-router-dom';
import { useFlags } from '../../hooks/useFlags';
import type { IsFlagEnabled } from '../FeatureFlagged/FeatureFlagged';

export type EggTab = {
  name: string;
  id: string;
  link: string;
  controls: string;
  attachment?: React.ReactNode;
  tooltip?: React.ReactNode;
  hideWhen?: (isFlagEnabled: IsFlagEnabled) => boolean;
};

export type EggTabsProps = {
  tabs: EggTab[];
  aside?: React.ReactNode;
  linkEnd?: boolean;
};

type EggTabProps = {
  tab: EggTab;
  linkEnd?: boolean;
};

function EggTabSection({ tab, linkEnd }: EggTabProps) {
  const { isFlagEnabled } = useFlags();

  if (tab.hideWhen && tab.hideWhen(isFlagEnabled)) {
    return null;
  }

  return (
    <li className="egg-tab-list-item">
      <NavLink
        to={tab.link}
        className={({ isActive }) => cls('egg-tab-link', { active: isActive })}
        role="presentation"
        end={linkEnd}
      >
        <span className="egg-tab-name">{tab.name}</span>
        {tab.attachment}
      </NavLink>

      {tab.tooltip}
    </li>
  );
}

export function EggTabs({ tabs, aside, linkEnd = false }: EggTabsProps) {
  return (
    <div className="egg-nav-wrapper">
      <ul className="egg-nav" role="tablist">
        {tabs.map((tab) => (
          <EggTabSection tab={tab} linkEnd={linkEnd} key={tab.id} />
        ))}
        <div className="egg-nav-mobile-section">{aside}</div>
      </ul>
      {aside && <aside className="egg-nav-aside">{aside}</aside>}
    </div>
  );
}

export function LabeledSection(props: PropsWithChildren<{}>) {
  return <dl className="labeled-section">{props.children}</dl>;
}

export type SectionLabelProps = {
  label: string;
  value: React.ReactNode;
};

function TabAttachment(props: PropsWithChildren<{}>) {
  return <span className="egg-tab-tag overline-small">{props.children}</span>;
}

export const TabNewTag = () => <TabAttachment>NEW</TabAttachment>;

export function SectionLabel(props: SectionLabelProps) {
  return (
    <>
      <dt className="labeled-section-label">{props.label}</dt>
      <dd className="labeled-section-value">{props.value}</dd>
    </>
  );
}
